import { ActionIcon, Button, Group, Popover, Text, TextInput, Tooltip } from "@mantine/core";
import { useId, useState, type FormEvent, type ReactNode } from "react";

export interface SaveNameProps {
  /** The button's tooltip and name: live's "Save generation", "Save" or "Saved". */
  label: string;
  icon: ReactNode;
  disabled: boolean;
  saving: boolean;
  /** Keeps the output under the name typed; `done` closes the form once it is kept. */
  onSave: (name: string, done: () => void) => void;
}

/**
 * Live's save with a name (Sounds, `save-name-popover` and `save-name-form`): the button opens
 * "Add name" with its "Name" field ("Please enter name"), Cancel and Save. An empty name says
 * "Name is required"; a refused save keeps the form open with the name typed, and the row says
 * why. The name goes as typed: the server makes it a file name (live's `normalizeFileName`).
 */
export function SaveName({ label, icon, disabled, saving, onSave }: SaveNameProps) {
  const [opened, setOpened] = useState(false);
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const titleId = useId();
  const close = () => {
    setOpened(false);
    setName("");
    setProblem(null);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() === "") {
      setProblem("Name is required");
      return;
    }
    onSave(name.trim(), close);
  };
  return (
    <Popover
      opened={opened}
      onChange={(next) => (next ? setOpened(true) : close())}
      position="bottom-end"
      width={360}
      withArrow
      shadow="md"
      trapFocus
      returnFocus
    >
      <Tooltip label={label} disabled={opened}>
        <Popover.Target>
          <ActionIcon
            variant="transparent"
            c="inherit"
            aria-label={label}
            disabled={disabled}
            loading={saving && opened}
            onClick={() => (opened ? close() : setOpened(true))}
          >
            {icon}
          </ActionIcon>
        </Popover.Target>
      </Tooltip>
      <Popover.Dropdown aria-labelledby={titleId}>
        <form onSubmit={submit} noValidate>
          <Text id={titleId} fw={500} mb={8}>
            Add name
          </Text>
          <TextInput
            label="Name"
            placeholder="Please enter name"
            value={name}
            error={problem}
            disabled={saving}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setProblem(null);
            }}
            mb={16}
            data-autofocus
          />
          <Group justify="flex-end" gap={8}>
            <Button type="button" variant="outline" disabled={saving} onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Save
            </Button>
          </Group>
        </form>
      </Popover.Dropdown>
    </Popover>
  );
}
